import type { OMSConfig, StrategyEvaluator, SignalResult, StrategyContext } from '../types';
import { ensureFlatOpts } from '../../analysis/trend-evaluator';
import { extractSnapshotOptions } from '../../../lib/snapshot-search';

/**
 * SRBounce — Mean Reversion Scalp Strategy
 *
 * How it works:
 * - Identifies the Support Strike (Highest PE OI/Volume in range)
 * - Identifies the Resistance Strike (Highest CE OI/Volume in range)
 * - If Index LTP drops to Support Strike, it Buys ATM CE.
 * - If Index LTP rises to Resistance Strike, it Buys ATM PE.
 */
export const evaluateSRBounce: StrategyEvaluator = (data: any, currentUnixTime: number, config: OMSConfig, context: StrategyContext): SignalResult | null => {
    if (!data) return null;

    const { symbol, expiry, activeRange, gap, settingsMode } = context;
    const spot = data.spotPrice || data.index_ltp || data.ltp || 0;
    if (spot <= 0) return null;

    const liveRawOptsArr = context.chain || extractSnapshotOptions(data, symbol, expiry) || [];
    const liveFlatOpts = ensureFlatOpts(liveRawOptsArr);
    if (!liveFlatOpts.length) return null;

    const atmStrike = Math.round(spot / gap) * gap;
    const minStrike = atmStrike - (activeRange * gap);
    const maxStrike = atmStrike + (activeRange * gap);

    const inRange = liveFlatOpts.filter((opt: any) => opt.strike >= minStrike && opt.strike <= maxStrike);
    
    // Choose evaluation metric based on settings
    const valKey = settingsMode === 'Top Volume' ? 'volume' : 'oi';

    // Find Support (Highest PE)
    const peOpts = inRange.filter((o: any) => o.type === 'PE');
    peOpts.sort((a, b) => (Number(b[valKey]) || 0) - (Number(a[valKey]) || 0));
    const supportStrike = peOpts[0]?.strike;

    // Find Resistance (Highest CE)
    const ceOpts = inRange.filter((o: any) => o.type === 'CE');
    ceOpts.sort((a, b) => (Number(b[valKey]) || 0) - (Number(a[valKey]) || 0));
    const resistanceStrike = ceOpts[0]?.strike;

    if (!supportStrike || !resistanceStrike) return null;

    // Define the threshold for triggering (e.g., within 20% of the strike gap)
    const triggerThreshold = gap * 0.2; 
    
    let status: 'BULL' | 'BEAR' | 'Sideways' = 'Sideways';
    let triggeredStrike = 0;
    let optionType: 'CE' | 'PE' = 'CE';

    // Check for bounces
    if (Math.abs(spot - supportStrike) <= triggerThreshold) {
        status = 'BULL'; // Bouncing off support -> Buy CE
        triggeredStrike = supportStrike;
        optionType = 'CE';
    } else if (Math.abs(spot - resistanceStrike) <= triggerThreshold) {
        status = 'BEAR'; // Rejecting off resistance -> Buy PE
        triggeredStrike = resistanceStrike;
        optionType = 'PE';
    }

    if (status === 'Sideways') return null;

    // Enter at ATM
    let entryPrice = 0;
    const atmRow = liveFlatOpts.find(r => r.strike === atmStrike && r.type === optionType);
    if (atmRow) {
        entryPrice = atmRow.ltp || atmRow.close || atmRow.price || 0;
    }
    
    if (entryPrice <= 0) return null;

    return {
        trade_type: status,
        strike: atmStrike,
        option_type: optionType,
        entry_price: entryPrice,
        remarks: `Bounce triggered at ${status === 'BULL' ? 'Support' : 'Resistance'} (${triggeredStrike}). Spot: ${spot.toFixed(2)}`
    };
};
